import { Search, X } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   The `type=search` variant of Input. Adds a leading search icon and a
   keyboard-accessible clear button in the trailing slot when the value is
   non-empty. Debounce policy lives in the caller — wrap in a hook if you
   want debounced onChange.
*/

export interface SearchInputProps
  extends Omit<React.ComponentPropsWithoutRef<"input">, "type" | "size"> {
  /** Accessible name if there is no visible label above. */
  "aria-label"?: string
  /** Fires when the clear button is pressed. */
  onClear?: () => void
  size?: "sm" | "md"
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput({ className, value, onClear, size = "md", ...props }, ref) {
    const showClear = Boolean(value) && (typeof value === "string" ? value.length > 0 : true)
    const dims = size === "sm" ? "h-7 text-body-sm" : "h-8.5 text-body"

    return (
      <div className={cn("relative flex items-center", className)}>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 size-4 text-text-3"
        />
        <input
          ref={ref}
          type="search"
          value={value}
          className={cn(
            "w-full rounded-md border border-border bg-surface pl-8 pr-8",
            "font-ui text-text placeholder:text-text-3",
            "transition-colors duration-120 ease-out",
            "hover:border-border-strong",
            "focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled",
            // hide the UA cancel button — we render our own
            "[&::-webkit-search-cancel-button]:appearance-none",
            dims,
          )}
          {...props}
        />
        {showClear && onClear && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={onClear}
            className={cn(
              "absolute right-1.5 grid size-6 place-items-center rounded-md text-text-3",
              "transition-colors duration-120 ease-out",
              "hover:bg-surface-sunken hover:text-text",
              "[&_svg]:size-3.5",
            )}
          >
            <X />
          </button>
        )}
      </div>
    )
  },
)
